"use client";
import Link from "next/link";
import logo from "@/assets/imgs/logo (1).png";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useParams, usePathname } from "next/navigation";
import Dashboard from "@/assets/icons/Dashboard";
import Ticket from "@/assets/icons/Ticket";
import Reports from "@/assets/icons/Reports";
import Invitations from "@/assets/icons/Invitations";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import Other from "@/assets/icons/Other";
import { useTranslations } from "next-intl";

export default function EventSidebar({ setShowEventSidebar }: any) {
    const t = useTranslations();
    const sidebarRef = useRef<HTMLElement>(null);
    const pathname = usePathname();
    const [openSections, setOpenSections] = useState([0, 1, 2, 3, 4]); // all open by default
    const { id: eventId } = useParams();
    // Handle click outside to close sidebar
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                setShowEventSidebar(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setShowEventSidebar]);

    // Handle link clicks to close sidebar
    const handleLinkClick = () => {
        setShowEventSidebar(false);
    };


    const sections = [
        {
            title: t("eventSidebar.events"),
            icon: <Dashboard />,
            items: [
                {
                    label: t("eventSidebar.eventData"),
                    href: `/business/event/details/${eventId}`,
                },
                { label: t("eventSidebar.eventPhotos"), href: `/business/event/photos/${eventId}` },
                {
                    label: t("eventSidebar.termsAndConditions"),
                    href: `/business/event/terms/${eventId}`,
                },
                { label: t("eventSidebar.sponsors"), href: `/business/event/sponsars/${eventId}` },
            ],
        },
        {
            title: t("eventSidebar.tickets"),
            icon: <Ticket />,
            items: [
                { label: t("eventSidebar.tickets"), href: `/business/event/tickets/${eventId}` },
                {
                    label: t("eventSidebar.soldTickets"),
                    href: `/business/event/tickets/${eventId}/selled`,
                },
            ],
        },
        {
            title: t("eventSidebar.reports"),
            icon: <Reports />,
            items: [
                { label: t("eventSidebar.dailyReports"), href: `/business/event/reports/daily/${eventId}` },
                { label: t("eventSidebar.packageReports"), href: `/business/event/reports/packages/${eventId}` },
            ],
        },
        {
            title: t("eventSidebar.invitations"),
            icon: <Invitations />,
            items: [
                {
                    label: t("eventSidebar.invitationDesign"),
                    href: `/business/event/invitations/design/${eventId}`,
                },
                { label: t("eventSidebar.invitationsList"), href: `/business/event/invitations/${eventId}` },
            ],
        },
        {
            title: t("eventSidebar.more"),
            icon: <Other />,
            items: [
                { label: t("eventSidebar.accounts"), href: `/business/event/accounts/${eventId}` },
                {
                    label: t("eventSidebar.sendNotifications"),
                    href: `/business/event/emails/${eventId}`,
                },
                {
                    label: t("eventSidebar.badgeDesign"),
                    href: `/business/event/designs/${eventId}`,
                },
            ],
        },
    ];

    const toggleSection = (idx: number) => {
        setOpenSections((prev) =>
            prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
        );
    };

    return (
        <aside
            ref={sidebarRef}
            className="fixed top-0 right-0 w-[200px] text-[#D1D3D4] px-3 pb-10 pt-4 h-full z-100 overflow-y-auto bg-[#111111]"
        >
            <div className="flex justify-between items-center gap-2 mb-10">
                <Link href="/" className="flex items-center">
                    <svg
                        viewBox="0 0 170 41"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-[100px] h-auto"
                    >
                        <g clipPath="url(#clip0_155_124_evt_sb)">
                            <path
                                d="M54.2228 3.5086C53.9876 3.5086 53.7666 3.45859 53.5527 3.37286C53.346 3.28714 53.1606 3.15855 53.0038 2.99424C52.847 2.82993 52.7187 2.64419 52.6331 2.43701C52.5405 2.2227 52.4977 1.99409 52.4977 1.7512C52.4977 1.50831 52.5405 1.28685 52.6331 1.06539C52.7187 0.858215 52.847 0.672474 53.0038 0.508165C53.1606 0.343855 53.346 0.222409 53.5527 0.129539C53.7666 0.0366683 53.9947 -0.00619507 54.2228 -0.00619507C54.4509 -0.00619507 54.6791 0.0438121 54.8929 0.129539C55.0997 0.215265 55.285 0.343855 55.4418 0.508165C55.5987 0.672474 55.727 0.858215 55.8125 1.06539C55.9052 1.2797 55.948 1.50831 55.948 1.7512C55.948 1.99409 55.9052 2.21555 55.8125 2.43701C55.727 2.64419 55.5987 2.82993 55.4418 2.99424C55.285 3.15855 55.0997 3.27999 54.8929 3.37286C54.6791 3.46573 54.4509 3.5086 54.2228 3.5086ZM54.2228 0.165258C53.3603 0.165258 52.6616 0.879647 52.6616 1.75834C52.6616 2.63704 53.3603 3.35143 54.2228 3.35143C55.0854 3.35143 55.784 2.63704 55.784 1.75834C55.784 0.879647 55.0854 0.165258 54.2228 0.165258Z"
                                fill="#D4AF37"
                            />
                            <path
                                d="M54.5436 1.89408C54.5436 1.89408 54.6077 1.87265 54.6363 1.85836C54.7147 1.8155 54.7788 1.75121 54.8216 1.67262C54.8644 1.59404 54.8858 1.50117 54.8858 1.39401C54.8858 1.28685 54.8644 1.19398 54.8216 1.1154C54.7788 1.03682 54.7147 0.972522 54.6363 0.929659C54.5578 0.886796 54.4652 0.865364 54.3582 0.865364H53.6311V2.70849H53.8877V1.9298H54.2727L54.6648 2.70849H54.9713L54.5436 1.90123V1.89408ZM53.8806 1.10826H54.3654C54.4153 1.10826 54.458 1.12254 54.4937 1.14398C54.5293 1.16541 54.5578 1.20113 54.5792 1.24399C54.6006 1.28685 54.6077 1.33686 54.6077 1.39401C54.6077 1.45116 54.6006 1.50117 54.5792 1.54403C54.5578 1.5869 54.5293 1.62262 54.4937 1.64405C54.458 1.66548 54.4153 1.67977 54.3654 1.67977H53.8806V1.10826Z"
                                fill="#D4AF37"
                            />
                            <path
                                d="M29.3936 5.53031L7.43715 11.424L8.4922 15.3746C9.2122 15.4032 9.91794 15.5246 10.5952 15.7318C13.2898 16.5462 15.5211 18.7036 16.2981 21.6255C17.2748 25.276 15.6922 29.0337 12.6482 30.9483L13.7033 34.8988L29.3864 30.6911V35.849L10.196 40.9997L6.66725 27.7907L9.06963 27.1477C10.9088 26.6548 11.9995 24.7545 11.5076 22.9185C11.0158 21.0754 9.11953 19.9824 7.28745 20.4753L4.88508 21.1183L3.9227 17.532L2.63954 12.7242L1.34924 7.91637L29.3936 0.372429V5.53031Z"
                                fill="#D4AF37"
                            />
                            <path
                                d="M58.8278 33.0914L29.3934 40.9926V35.8418L52.7471 29.5695L51.692 25.6189C50.972 25.5903 50.2591 25.4689 49.5819 25.2617C46.8873 24.4473 44.6631 22.2899 43.879 19.368C42.9023 15.7104 44.4849 11.9598 47.5288 10.0452L46.4738 6.09468L29.3934 10.6811V5.53031L49.9811 0.000946045L53.5098 13.21L51.1075 13.8529C49.2683 14.3459 48.1776 16.2461 48.6694 18.0821C49.1613 19.9252 51.0576 21.0183 52.8896 20.5253L55.292 19.8824L56.2544 23.4686L57.5375 28.2764L58.8207 33.0843L58.8278 33.0914Z"
                                fill="#D4AF37"
                            />
                            <path
                                d="M85.5605 8.23128L85.2968 9.75293C84.8191 12.4819 83.6073 14.6179 81.6469 16.161C79.6865 17.7112 77.3411 18.4828 74.5966 18.4828C71.852 18.4828 69.5495 17.5612 67.9954 15.7181C66.4413 13.875 65.8924 11.6318 66.3487 8.98853C66.8049 6.38102 68.0596 4.23071 70.1055 2.53761C72.1514 0.844504 74.5467 0.00152588 77.2841 0.00152588C79.109 0.00152588 80.6845 0.373008 82.0033 1.11597C83.3221 1.85894 84.3201 2.90909 85.0045 4.26643L81.1336 6.22385C80.2924 4.71649 78.8595 3.96638 76.835 3.96638C75.2382 3.96638 73.8623 4.45217 72.7146 5.41659C71.5669 6.38102 70.8469 7.65977 70.5689 9.25286C70.2695 10.8459 70.5261 12.1247 71.3459 13.0891C72.1657 14.0535 73.3847 14.5393 75.0243 14.5393C77.6619 14.5393 79.4584 13.6249 80.4279 11.7961H75.6873L76.3217 8.23843H85.5677L85.5605 8.23128Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M94.9918 18.4822C92.3185 18.4822 90.2013 17.5535 88.6258 15.6889C87.0504 13.8244 86.5157 11.5955 87.0076 8.98795C87.4853 6.34472 88.7399 4.18726 90.7858 2.51559C92.8318 0.836781 95.284 0.000946045 98.1498 0.000946045C100.787 0.000946045 102.89 0.929651 104.459 2.79421C106.034 4.65876 106.583 6.88765 106.105 9.49517C105.614 12.1384 104.352 14.2959 102.313 15.9675C100.274 17.6463 97.8361 18.4822 94.9846 18.4822H94.9918ZM95.4409 14.5245C96.9878 14.5245 98.3779 14.0315 99.6183 13.0528C100.859 12.0741 101.614 10.8025 101.899 9.24513C102.178 7.68777 101.899 6.41616 101.073 5.43744C100.238 4.45873 99.0551 3.9658 97.5296 3.9658C96.004 3.9658 94.6425 4.45873 93.4448 5.43744C92.2472 6.41616 91.513 7.68777 91.2278 9.24513C90.9498 10.8168 91.1993 12.0955 91.9906 13.0671C92.7819 14.0387 93.9296 14.5245 95.4409 14.5245Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M111.823 14.4173H118.41L117.697 18.4822H106.904L107.624 14.3959L110.162 0.000946045H114.361L111.823 14.4173Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M130.193 0.000946045C132.617 0.000946045 134.563 0.936795 136.032 2.80135C137.5 4.6659 138.006 6.90194 137.55 9.50946C137.094 12.1313 135.882 14.2816 133.914 15.9675C131.947 17.6463 129.63 18.4893 126.957 18.4893H119.557L122.815 0.00808993H130.193V0.000946045ZM133.465 9.1094C133.743 7.54489 133.515 6.309 132.781 5.41601C132.04 4.51588 130.935 4.06582 129.459 4.06582H126.294L124.462 14.4173H127.37C128.931 14.4173 130.272 13.9315 131.377 12.9671C132.482 11.9955 133.18 10.7168 133.458 9.1094H133.465Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M152.307 4.06582H145.057L144.508 7.15198H151.123L150.41 11.1097H143.809L143.225 14.4102H150.603L149.89 18.475H138.306L141.564 -0.00619507H153.005L152.292 4.05868L152.307 4.06582Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M152.278 18.4822L155.564 0.000946045H158.715L163.998 10.381L165.801 0.000946045H170L166.771 18.4822H163.606L158.337 8.10211L156.477 18.4822H152.278Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M79.9076 22.5113L79.5155 24.8331H74.0621L71.2248 40.9926H68.7868L71.624 24.8331H66.1991L66.5912 22.5113H79.9076Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M82.3455 22.5113H84.7835L81.5257 40.9997H79.0876L82.3455 22.5113Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M94.1649 40.9926C91.3704 40.9926 89.2247 40.0567 87.7205 38.1922C86.2164 36.3276 85.7031 34.0916 86.1807 31.4983C86.637 28.9194 87.8987 26.7762 89.9732 25.0688C92.0476 23.3615 94.4001 22.5113 97.0377 22.5113C98.827 22.5113 100.374 22.9043 101.664 23.6829C102.955 24.4616 103.917 25.5332 104.551 26.8905L102.363 28.0121C101.329 25.8761 99.5114 24.8045 96.9094 24.8045C94.8207 24.8045 92.9958 25.4689 91.4417 26.7905C89.8876 28.1121 88.9324 29.7695 88.5831 31.7555C88.2338 33.7558 88.5974 35.4132 89.6738 36.7205C90.7573 38.0279 92.3185 38.6779 94.3787 38.6779C95.7118 38.6779 96.9451 38.3922 98.0643 37.8135C99.1906 37.2349 100.117 36.4276 100.859 35.3918L102.89 36.6419C101.892 37.985 100.616 39.0423 99.0837 39.8281C97.5439 40.6068 95.9114 40.9997 94.172 40.9997L94.1649 40.9926Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M121.411 22.5113L111.502 31.6198L118.303 40.9926H115.565L108.957 31.8841L107.353 40.9926H104.915L108.173 22.5042H110.611L109.149 30.8197L118.31 22.5042H121.418L121.411 22.5113Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M134.185 24.8331H125.752L124.747 30.5125H132.517L132.097 32.8128H124.341L123.3 38.6779H131.861L131.469 40.9997H120.455L123.728 22.5113H134.585L134.192 24.8331H134.185Z"
                                fill="#D1D3D4"
                            />
                            <path
                                d="M149.469 22.5113L149.077 24.8331H143.624L140.787 40.9926H138.349L141.186 24.8331H135.761L136.153 22.5113H149.469Z"
                                fill="#D1D3D4"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_155_124_evt_sb">
                                <rect width="170" height="41" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </Link>

                <svg onClick={() => {
                    setShowEventSidebar(false);
                }} width="20" height="20" className="cursor-pointer shrink-0" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.7 21.4407L14.5 15.6407L20.3 21.4407L21.4407 20.3L15.6407 14.5L21.4407 8.7L20.3 7.55933L14.5 13.3593L8.7 7.55933L7.55933 8.7L13.3593 14.5L7.55933 20.3L8.7 21.4407ZM14.5048 29C12.5006 29 10.6156 28.6198 8.84983 27.8593C7.08513 27.0978 5.54974 26.0646 4.24367 24.7596C2.93759 23.4546 1.9038 21.9208 1.14228 20.1582C0.380759 18.3957 0 16.5112 0 14.5048C0 12.4985 0.380759 10.6135 1.14228 8.84983C1.90272 7.08513 2.93437 5.54974 4.23722 4.24367C5.54007 2.93759 7.07439 1.9038 8.84017 1.14228C10.6059 0.380759 12.4909 0 14.4952 0C16.4994 0 18.3844 0.380759 20.1502 1.14228C21.9149 1.90272 23.4503 2.93491 24.7563 4.23883C26.0624 5.54276 27.0962 7.07707 27.8577 8.84178C28.6192 10.6065 29 12.4909 29 14.4952C29 16.4994 28.6198 18.3844 27.8593 20.1502C27.0989 21.9159 26.0656 23.4513 24.7596 24.7563C23.4535 26.0613 21.9197 27.0951 20.1582 27.8577C18.3967 28.6203 16.5123 29.0011 14.5048 29Z" fill="white" />
                </svg>

            </div>
            <nav className="flex-1 flex flex-col gap-2 ">
                {sections.map((section, idx) => {
                    const isSectionActive = section.items.some(item => pathname === item.href);

                    return (
                        <div key={section.title} className="mb-4">
                            <button
                                className="flex items-center justify-between w-full py-2 text-lg focus:outline-none"
                                onClick={() => toggleSection(idx)}
                            >
                                <div className={`flex items-center gap-2 ml-auto ${isSectionActive ? 'text-[#d9ac4f]' : 'text-[#D1D3D4]'}`}>
                                    {section.icon}
                                    <span className={`font-normal`}>
                                        {section.title}
                                    </span>
                                </div>
                                <span>
                                    {openSections.includes(idx) ? (
                                        <ChevronDownIcon className="w-5 h-5 text-[#d9ac4f]" />
                                    ) : (
                                        <ChevronUpIcon className="w-5 h-5 text-[#D1D3D4]/80" />
                                    )}
                                </span>
                            </button>
                            {openSections.includes(idx) && (
                                <ul className="mt-2 space-y-1 pr-2">
                                    {section.items.map((item) => (
                                        <li key={item.label} dir="rtl">
                                            <Link
                                                href={item.href}
                                                onClick={handleLinkClick}
                                                className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors`}
                                            >
                                                <span
                                                    className={`w-2 h-2 rounded-full ${pathname === item.href ? "bg-[#d9ac4f]" : "glass-effect/40"
                                                        }`}
                                                ></span>
                                                <span
                                                    className={`font-normal text-base ${pathname === item.href
                                                        ? "text-[#d9ac4f]"
                                                        : "text-[#D1D3D4]"
                                                        }`}
                                                >
                                                    {item.label}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </nav>
        </aside>
    );
}